import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XCircleIcon,
  EyeIcon,
  UserIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  ArrowPathIcon,
  DocumentMagnifyingGlassIcon,
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  FolderOpenIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext';
import {
  computeTransactionRisk,
  transactionRiskLevelColor,
  transactionRiskBarClass,
} from './payoutTransactionRisk';
import OcrExtractPanel from '../../components/OcrExtractPanel';
import PatronComplianceStrip from '../../components/patron/PatronComplianceStrip';
import { payoutMonitoringActionsAllowed } from './payoutMonitoringQueue';
import HelpScreenButton from '../../components/help/HelpScreenButton';
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel';
import { primaryHelpSectionForPayoutMonitoring } from './content/payoutMonitoringScreenHelp';
import {
  ID_LABELS,
  displayMembershipNumber,
  displayPlayerId,
  staffMemberLookupKey,
} from '../../utils/patronIdentity';

/** AU floor cash cap — keep in sync with backend `PAYOUT_CASH_DESK_CAP_AUD` (payout-monitoring.js). */
const PAYOUT_CASH_DESK_CAP_AUD = 5000
const PAGE_SIZE = 50
const PAYOUT_HELP_SCREEN_KEY = 'page:payout-safe/monitoring'

const payoutDetailRowGrid = 'grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-2 items-start py-1.5'
const payoutDetailLabel = 'text-gray-600 leading-snug'
const payoutDetailValue = 'text-gray-900 min-w-0 break-words'

function PayoutDetailRow({ label, children, rowClassName = '', labelClassName = '', valueClassName = '' }) {
  return (
    <div className={`${payoutDetailRowGrid} ${rowClassName}`}>
      <span className={`${payoutDetailLabel} ${labelClassName}`}>{label}</span>
      <div className={`${payoutDetailValue} ${valueClassName}`}>{children}</div>
    </div>
  )
}

/** Masked bank account — click toggles full number when available from mobile capture. */
function RevealableBankAccount({ fullAccount, last4, revealed, onToggle, prefix = '' }) {
  const digits = fullAccount != null ? String(fullAccount).replace(/\s/g, '') : ''
  const masked =
    last4 != null && String(last4).trim() !== ''
      ? String(last4).replace(/\D/g, '') || String(last4)
      : digits.length >= 4
        ? digits.slice(-4)
        : digits
  if (!digits && !masked) return null
  const canReveal = digits.length > 4
  const display = revealed && canReveal ? digits : `…${masked || digits}`
  if (!canReveal) {
    return (
      <span className="font-mono">
        {prefix}
        {display}
      </span>
    )
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      className="font-mono text-left underline decoration-dotted underline-offset-2 hover:text-psg-teal focus:outline-none focus-visible:ring-1 focus-visible:ring-psg-teal rounded"
      title={revealed ? 'Click to hide account number' : 'Click to show full account number'}
      aria-pressed={revealed}
    >
      {prefix}
      {display}
    </button>
  )
}

const QUEUE_TABS = [
  { id: 'open', label: 'Open queue', description: 'Needs floor/compliance review — no active investigation yet' },
  { id: 'investigating', label: 'Under investigation', description: 'Linked to an open investigation case' },
  { id: 'smr', label: 'SMR', description: 'SMR draft submitted or filed with AUSTRAC' },
  { id: 'closed', label: 'Closed / compliant', description: 'Reviewed and dismissed, or investigation closed' },
  { id: 'all', label: 'All payouts', description: 'Full history with pagination' },
]

function playerTypeLabel(playerType) {
  if (playerType === 'member') return 'Member'
  if (playerType === 'visitor') return 'Visitor'
  return 'Uncarded'
}

function playerTypeBadgeClass(playerType) {
  if (playerType === 'member') return 'bg-blue-100 text-blue-800'
  if (playerType === 'visitor') return 'bg-gray-100 text-gray-700'
  return 'bg-amber-100 text-amber-900'
}

/** Patron master profile id from row, patron_resolve snapshot, or mobile capture. */
function effectiveMasterProfileId(payout) {
  if (!payout) return null
  const direct = payout.masterProfileId || payout.master_profile_id
  if (direct != null && String(direct).trim() !== '') return String(direct).trim()
  const fromResolve =
    payout.patronResolve?.master_profile_id ||
    payout.mobileCapture?.patron_resolve?.master_profile_id
  if (fromResolve != null && String(fromResolve).trim() !== '') return String(fromResolve).trim()
  return null
}

function clubIdSubtext(payout) {
  if (payout.playerType === 'visitor') {
    const parts = []
    if (payout.visitId) parts.push(`Visit ${payout.visitId}`)
    if (payout.visitorId) parts.push(`VMS ${payout.visitorId}`)
    return parts.length ? parts.join(' · ') : 'No visit id'
  }
  const club = displayMembershipNumber(payout) || payout.membershipNumber || payout.memberId
  if (payout.playerType === 'member' && club) {
    const player = displayPlayerId(payout) || payout.playerId
    if (player && player !== club) {
      return `${ID_LABELS.membership} ${club} · ${ID_LABELS.player} ${player}`
    }
    return `${ID_LABELS.membership} ${club}`
  }
  return 'No club id'
}

function patronIdSummary(payout) {
  const parts = []
  const patronId = effectiveMasterProfileId(payout)
  if (patronId) parts.push(`Patron ${patronId}`)
  const club = clubIdSubtext(payout)
  if (club && club !== 'No club id') parts.push(club)
  if (parts.length) return parts.join(' · ')
  return club
}

function effectivePatronRiskLevel(payout) {
  if (!payout) return null
  const candidates = [
    payout.riskLevel,
    payout.risk_level,
    payout.patronResolve?.patron?.risk_level,
    payout.mobileCapture?.patron_resolve?.patron?.risk_level,
  ]
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim()
  }
  return null
}

/** Canonical patron risk for display — overview compliance strip, then API-hydrated profile risk. */
function patronRiskScoreForDisplay(payout, compliance) {
  if (compliance?.risk_score != null && Number.isFinite(Number(compliance.risk_score))) {
    return Number(compliance.risk_score)
  }
  return effectivePatronRiskScore(payout)
}

function patronRiskLevelForDisplay(payout, compliance) {
  if (compliance?.risk_level != null && String(compliance.risk_level).trim() !== '') {
    return String(compliance.risk_level).trim()
  }
  return effectivePatronRiskLevel(payout)
}

function patronComplianceLookupKey(payout) {
  if (!payout) return null
  return (
    payout.masterProfileId ||
    payout.membershipNumber ||
    payout.playerId ||
    payout.memberId ||
    staffMemberLookupKey(payout) ||
    null
  )
}

/** Patron phone status for payout modal — capture, declined, profile/prior fallback. */
function resolvePatronPhoneDisplay(payout, mc, profilePhone) {
  const refused =
    payout?.patronPhoneRefused === true ||
    payout?.effectivePatronPhoneRefused === true ||
    mc?.patron_phone_refused === true
  if (refused) {
    return { status: 'declined', phone: null, hint: null }
  }

  const capturePhone = mc?.patron_phone != null ? String(mc.patron_phone).trim() : ''
  const effectivePhone =
    payout?.effectivePatronPhone != null ? String(payout.effectivePatronPhone).trim() : ''
  const profile = profilePhone != null ? String(profilePhone).trim() : ''
  const phone = capturePhone || effectivePhone || profile

  const fromPrior =
    !capturePhone &&
    phone &&
    Array.isArray(payout?.effectivePatronPhoneSources) &&
    payout.effectivePatronPhoneSources.some((s) =>
      ['patron_profiles', 'prior_payout_capture'].includes(s)
    )

  if (phone) {
    return {
      status: fromPrior ? 'on_record' : 'captured',
      phone,
      hint: fromPrior ? 'From patron record' : null,
    }
  }
  if (payout?.patronPhoneRefused === false) {
    return { status: 'not_captured', phone: null, hint: null }
  }
  return { status: 'unknown', phone: null, hint: null }
}

function effectivePatronAddressText(payout, mc) {
  if (mc?.patron_address != null && String(mc.patron_address).trim() !== '') {
    return String(mc.patron_address).trim()
  }
  const ocr = payout?.effectiveOcrData || payout?.payoutOcrData || payout?.queueOcrData
  const line = ocr?.address_line
  if (!line) return null
  const extra = [ocr.address_suburb, ocr.address_state, ocr.address_postcode]
    .filter(Boolean)
    .join(' ')
  return [line, extra].filter(Boolean).join(', ')
}

/** Patron/profile risk: canonical profile row, then payout-time snapshot, then mobile resolve. */
function effectivePatronRiskScore(payout) {
  if (!payout) return null
  const candidates = [
    payout.patronProfileRiskScore,
    payout.riskScore,
    payout.risk_score,
    payout.payoutRiskScoreAtCapture,
    payout.patronResolve?.patron?.risk_score,
    payout.patronResolve?.patron?.riskScore,
    payout.mobileCapture?.patron_resolve?.patron?.risk_score,
    payout.mobileCapture?.patron_resolve?.patron?.riskScore,
  ]
  for (const c of candidates) {
    if (c != null && c !== '' && Number.isFinite(Number(c))) return Number(c)
  }
  return null
}

function withPayoutRiskFields(payout, txnRiskConfig) {
  if (!payout) return payout
  const split = effectivePayoutSplit(payout)
  const transactionRisk =
    payout.transactionRisk || computeTransactionRisk(payout, split, txnRiskConfig)
  const riskScore = effectivePatronRiskScore(payout)
  return {
    ...payout,
    riskScore: riskScore ?? payout.riskScore ?? null,
    transactionRisk,
    transactionRiskScore: transactionRisk?.score ?? payout.transactionRiskScore ?? null,
  }
}

function formatPatronResolvePatron(pr) {
  const p = pr?.patron
  if (!p || typeof p !== 'object') return null
  const first = p.firstname || p.first_name || p.FirstName || ''
  const last = p.lastname || p.last_name || p.LastName || ''
  const name = [first, last].filter(Boolean).join(' ')
  return {
    name: name || null,
    dob: p.dob || p.date_of_birth || p.DateOfBirth || null,
    tier: p.tier_level || p.TierLevel || null,
    status: p.status || p.Status || null,
    responseCode: p.response_code || p.ResponseCode || pr.response_code || null,
  }
}

/**
 * Derive split from total + payment method + optional mobile capture numbers.
 * Matches backend `derivePaymentSplitAmounts` behaviour for display.
 */
function deriveSplitFromAmountAndMethod(total, pmRaw, cashPaid, balance) {
  if (!Number.isFinite(total) || total < 0) return null
  const pm = String(pmRaw || '').trim()
  const pmLower = pm.toLowerCase()
  const isCheque = pmLower === 'cheque'
  const isEft = pmLower === 'eft'
  const isCash = pmLower === 'cash'

  if (isCheque) {
    if (total > PAYOUT_CASH_DESK_CAP_AUD) {
      return {
        cash: PAYOUT_CASH_DESK_CAP_AUD,
        eft: 0,
        cheque: Math.max(0, total - PAYOUT_CASH_DESK_CAP_AUD),
      }
    }
    const c = Number.isFinite(cashPaid) ? Math.min(cashPaid, total) : 0
    const q = Number.isFinite(balance) ? balance : Math.max(0, total - c)
    return { cash: c, eft: 0, cheque: q }
  }

  if (total > PAYOUT_CASH_DESK_CAP_AUD) {
    if (isEft && !Number.isFinite(cashPaid)) {
      return { cash: 0, eft: total, cheque: 0 }
    }
    if (isEft && Number.isFinite(cashPaid) && cashPaid <= 0) {
      return { cash: 0, eft: total, cheque: 0 }
    }
    const cRaw = Number.isFinite(cashPaid)
      ? cashPaid
      : isCash || !pm
        ? PAYOUT_CASH_DESK_CAP_AUD
        : 0
    const c = Math.min(PAYOUT_CASH_DESK_CAP_AUD, Math.max(0, cRaw), total)
    let e = Number.isFinite(balance) ? balance : Math.max(0, total - c)
    if (e + c > total + 0.0001) e = Math.max(0, total - c)
    return { cash: c, eft: e, cheque: 0 }
  }

  if (isCash) {
    const c = Number.isFinite(cashPaid) ? cashPaid : total
    return { cash: Math.min(c, total), eft: 0, cheque: 0 }
  }
  if (isEft) {
    const c = Number.isFinite(cashPaid) ? Math.min(cashPaid, total) : 0
    const e = Number.isFinite(balance) ? balance : Math.max(0, total - c)
    return { cash: c, eft: e, cheque: 0 }
  }

  return {
    cash: Number.isFinite(cashPaid) ? Math.min(cashPaid, total) : total,
    eft: Number.isFinite(balance) ? balance : 0,
    cheque: 0,
  }
}

/**
 * Prefer DB split columns; if missing or all zero with amount > 0, derive from mobile capture or amount + paymentMethod.
 */
function effectivePayoutSplit(payout) {
  const totalAmt = payout.amount != null ? Number(payout.amount) : NaN

  const c0 = payout.cashAmount
  const e0 = payout.eftAmount
  const q0 = payout.chequeAmount
  const hasCol = c0 != null || e0 != null || q0 != null
  if (hasCol) {
    const cash = Number(c0) || 0
    const eft = Number(e0) || 0
    const cheque = Number(q0) || 0
    const sum = cash + eft + cheque
    if (sum > 0.0001 || !Number.isFinite(totalAmt) || totalAmt <= 0) {
      return { cash, eft, cheque, source: 'record' }
    }
    // Stored 0/0/0 but payout has amount — treat as no split and derive below
  }

  const m = payout.mobileCapture && typeof payout.mobileCapture === 'object' ? payout.mobileCapture : null
  const pm = String((m && m.payment_method) || payout.paymentMethod || '').trim()
  const cashPaid =
    m && m.cash_amount_paid != null && m.cash_amount_paid !== ''
      ? Number(m.cash_amount_paid)
      : null
  const balance =
    m && m.balance_to_pay != null && m.balance_to_pay !== ''
      ? Number(m.balance_to_pay)
      : null

  const d = deriveSplitFromAmountAndMethod(totalAmt, pm, cashPaid, balance)
  if (!d) return null
  return {
    ...d,
    source: m ? 'mobile_capture' : 'derived',
  }
}

/** Non-zero cash / EFT / cheque flags for payment-mode filtering. */
function payoutPaymentModeFlags(payout) {
  const split = effectivePayoutSplit(payout)
  if (split) {
    return {
      cash: (split.cash || 0) > 0.0001,
      eft: (split.eft || 0) > 0.0001,
      cheque: (split.cheque || 0) > 0.0001,
    }
  }
  const m = payout.mobileCapture && typeof payout.mobileCapture === 'object' ? payout.mobileCapture : null
  const pm = String((m && m.payment_method) || payout.paymentMethod || '').trim().toLowerCase()
  if (pm === 'eft') return { cash: false, eft: true, cheque: false }
  if (pm === 'cheque') return { cash: false, eft: false, cheque: true }
  if (pm === 'cash') return { cash: true, eft: false, cheque: false }
  return null
}

function payoutMatchesPaymentModeFilter(payout, filter) {
  if (!filter || filter === 'all') return true
  const flags = payoutPaymentModeFlags(payout)
  if (!flags) return false
  const { cash, eft, cheque } = flags
  const methodCount = (cash ? 1 : 0) + (eft ? 1 : 0) + (cheque ? 1 : 0)
  switch (filter) {
    case 'cash':
      return cash && !eft && !cheque
    case 'eft':
      return eft && !cash && !cheque
    case 'cheque':
      return cheque && !cash && !eft
    case 'split':
      return methodCount >= 2
    case 'has_cash':
      return cash
    case 'non_cash':
      return !cash && (eft || cheque)
    default:
      return true
  }
}

function formatEftSummary(eft) {
  if (!eft || typeof eft !== 'object') return null
  const region = eft.region ? String(eft.region) : ''
  const bank = eft.bank_name ? String(eft.bank_name) : ''
  const bsb = eft.bsb ? String(eft.bsb) : ''
  const acct = eft.account_number != null ? String(eft.account_number).replace(/\s/g, '') : ''
  const tail = acct.length >= 4 ? acct.slice(-4) : acct
  const parts = []
  if (region) parts.push(region === 'australian' || region === 'Australian' ? 'Australian' : 'Overseas')
  if (bank) parts.push(bank)
  if (bsb) parts.push(`BSB ${bsb}`)
  if (!parts.length && !tail && !acct) return null
  return {
    prefix: parts.join(' · '),
    accountNumber: acct || null,
    accountLast4: tail || null,
    /** Plain string for any non-UI callers */
    text: [...parts, tail ? `Acct …${tail}` : null].filter(Boolean).join(' · ') || null,
  }
}

function formatChequeSummary(ch) {
  if (!ch || typeof ch !== 'object') return null
  const name = ch.name_on_cheque ? String(ch.name_on_cheque) : ''
  const dt = ch.cheque_date ? String(ch.cheque_date) : ''
  const hasPhoto = !!(ch.cheque_photo || ch.photo_jpeg_base64)
  const bits = []
  if (name) bits.push(name)
  if (dt) bits.push(dt)
  if (hasPhoto) bits.push('Cheque photo on file')
  return bits.length ? bits.join(' · ') : null
}

/** Signature tile from tablet POST (after blob upload: `image.url`, or legacy base64). */
function formatDeclarationAcceptedAt(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return String(iso)
  }
}

const PayoutMonitoring = () => {
  const navigate = useNavigate();
  const { user } = useAuth()
  const [payoutData, setPayoutData] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [revealBankAccount, setRevealBankAccount] = useState(false);
  const [payoutModalTab, setPayoutModalTab] = useState('details');
  const [captureAttachments, setCaptureAttachments] = useState(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState(null);
  const [creatingCase, setCreatingCase] = useState(false);
  const [creatingEcdd, setCreatingEcdd] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [reviewCommentDraft, setReviewCommentDraft] = useState('');
  const [showMiniInvestigation, setShowMiniInvestigation] = useState(false);
  const [miniInvestigationData, setMiniInvestigationData] = useState(null);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);
  const [listMeta, setListMeta] = useState({ total: null, returned: null, page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    venue: 'all',
    payoutTier: 'all',
    queueTab: 'open',
    kycStatus: 'all',
    paymentMode: 'all',
    riskLevel: 'all',
    playerType: 'all', // 'member' or 'visitor'
    patronContext: 'all', // watchlist | smr | ecdd | investigation
    patronMemberId: '',
    patronMasterProfileId: '',
    patronSearch: '',
    dateRange: 'custom',
    startDate: '',
    endDate: '',
    amountRange: 'all'
  });
  const [patronTypeahead, setPatronTypeahead] = useState({ results: [], loading: false, open: false });
  const [payoutPatronCompliance, setPayoutPatronCompliance] = useState(null);
  const [payoutComplianceLoading, setPayoutComplianceLoading] = useState(false);
  const patronSearchDebounceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' }); // Default: newest first
  /** Venue names from GET /api/venues — matches payout_records.venue; no hardcoded demo venues */
  const [venueNames, setVenueNames] = useState([]);
  const [txnRiskConfig, setTxnRiskConfig] = useState(null);
  const txnRiskConfigRef = useRef(null);
  const payoutLoadSeqRef = useRef(0);

  useEffect(() => {
    txnRiskConfigRef.current = txnRiskConfig;
  }, [txnRiskConfig]);

  const refreshTxnRiskConfig = useCallback(async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/venue-config/transaction-risk'));
      if (!r.ok) return null;
      const j = await r.json();
      if (j?.success && j.config) {
        setTxnRiskConfig(j.config);
        return j.config;
      }
    } catch {/* fall back to defaults inside computeTransactionRisk */}
    return null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await refreshTxnRiskConfig();
      if (!cancelled && cfg) txnRiskConfigRef.current = cfg;
    })();
    return () => { cancelled = true };
  }, [refreshTxnRiskConfig]);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'));
        const j = await r.json();
        if (j.success && Array.isArray(j.data)) {
          const names = j.data
            .map((v) => v.name)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          setVenueNames(names);
        }
      } catch (_) {
        setVenueNames([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (filters.venue !== 'all' && venueNames.length > 0 && !venueNames.includes(filters.venue)) {
      setFilters((f) => ({ ...f, venue: 'all' }));
    }
  }, [venueNames, filters.venue]);

  /** Re-score rows when transaction-risk config loads (does not re-fetch the list). */
  useEffect(() => {
    if (!txnRiskConfig || payoutData.length === 0) return;
    setPayoutData((rows) => rows.map((row) => withPayoutRiskFields(row, txnRiskConfig)));
  }, [txnRiskConfig]);

  const loadPayoutData = useCallback(async ({ refreshRiskConfig = false } = {}) => {
    const seq = ++payoutLoadSeqRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.venue !== 'all') params.append('venue', filters.venue);
      if (filters.payoutTier !== 'all') params.append('tier', filters.payoutTier);
      params.append('queue_tab', filters.queueTab || 'open');
      params.append('page', String(page));
      params.append('limit', String(PAGE_SIZE));
      if (filters.startDate) params.append('dateFrom', filters.startDate);
      if (filters.endDate) params.append('dateTo', filters.endDate);
      if (filters.patronMemberId) {
        params.append('member_id', filters.patronMemberId);
      } else if (filters.patronMasterProfileId) {
        params.append('master_profile_id', filters.patronMasterProfileId);
      } else if (filters.patronSearch.trim()) {
        params.append('search', filters.patronSearch.trim());
      }
      if (filters.patronContext !== 'all') {
        params.append('patron_context', filters.patronContext);
      }
      params.append('enrich', '1');

      const qs = params.toString();

      const payoutsPromise = authFetch(getLocalApiUrl(`/api/monitoring/payouts?${qs}`));
      const configPromise = refreshRiskConfig ? refreshTxnRiskConfig() : Promise.resolve(null);
      const [payoutsRes, refreshedConfig] = await Promise.all([payoutsPromise, configPromise]);
      if (seq !== payoutLoadSeqRef.current) return;

      const riskConfig = refreshedConfig || txnRiskConfigRef.current;
      const payoutsJson = await payoutsRes.json();
      if (payoutsJson.success) {
        const meta = payoutsJson.meta || {};
        const apiTotal = meta.total != null ? Number(meta.total) : null;
        const rowCount = Array.isArray(payoutsJson.data) ? payoutsJson.data.length : 0;
        setListMeta({
          total: apiTotal,
          returned: meta.returned != null ? Number(meta.returned) : rowCount,
          page: meta.page != null ? Number(meta.page) : page,
          totalPages:
            meta.totalPages != null
              ? Number(meta.totalPages)
              : apiTotal != null
                ? Math.max(1, Math.ceil(apiTotal / PAGE_SIZE))
                : rowCount >= PAGE_SIZE
                  ? page + 1
                  : 1,
          queueTab: filters.queueTab,
        });
        let rows = Array.isArray(payoutsJson.data) ? payoutsJson.data : [];
        rows = rows.map((row) => {
          const masterProfileId = effectiveMasterProfileId(row);
          return withPayoutRiskFields(
            {
              ...row,
              masterProfileId: masterProfileId || row.masterProfileId || null,
              playerType: row.playerType || 'unknown',
              onWatchlist: row.onWatchlist ?? false,
              watchlistData: row.watchlistData,
              hasCases: row.hasCases ?? false,
              hasSmrCase: row.hasSmrCase ?? false,
              hasOpenEcdd: row.hasOpenEcdd ?? false,
              caseCount: row.caseCount ?? 0,
              cases: row.cases ?? [],
              ecddRecords: row.ecddRecords ?? [],
              linkedCase: row.linkedCase ?? null,
              workflowLabel: row.workflowLabel ?? null,
            },
            riskConfig
          );
        });
        
        // Apply client-side filters for KYC, payment mode, risk level, and player type
        if (filters.kycStatus !== 'all') {
          rows = rows.filter(r => r.kycStatus === filters.kycStatus);
        }
        if (filters.paymentMode !== 'all') {
          rows = rows.filter((r) => payoutMatchesPaymentModeFilter(r, filters.paymentMode));
        }
        if (filters.riskLevel !== 'all') {
          if (filters.riskLevel === 'high') rows = rows.filter(r => r.riskScore >= 70);
          else if (filters.riskLevel === 'medium') rows = rows.filter(r => r.riskScore >= 40 && r.riskScore < 70);
          else if (filters.riskLevel === 'low') rows = rows.filter(r => r.riskScore < 40);
        }
        if (filters.playerType !== 'all') {
          rows = rows.filter(r => r.playerType === filters.playerType);
        }
        
        if (seq === payoutLoadSeqRef.current) setPayoutData(rows);
      }
    } catch (error) {
      console.error('Error loading payout data:', error);
    } finally {
      if (seq === payoutLoadSeqRef.current) setLoading(false);
    }
  }, [filters, page, refreshTxnRiskConfig]);

  useEffect(() => {
    loadPayoutData();
  }, [loadPayoutData]);

  useEffect(() => {
    const q = filters.patronSearch.trim();
    if (filters.patronMemberId || filters.patronMasterProfileId || q.length < 2) {
      setPatronTypeahead({ results: [], loading: false, open: false });
      return undefined;
    }
    if (patronSearchDebounceRef.current) clearTimeout(patronSearchDebounceRef.current);
    patronSearchDebounceRef.current = setTimeout(async () => {
      setPatronTypeahead((s) => ({ ...s, loading: true }));
      try {
        const r = await authFetch(getLocalApiUrl(`/api/members/search?q=${encodeURIComponent(q)}`));
        const j = await r.json();
        setPatronTypeahead({ results: j.data || [], loading: false, open: true });
      } catch {
        setPatronTypeahead({ results: [], loading: false, open: false });
      }
    }, 300);
    return () => {
      if (patronSearchDebounceRef.current) clearTimeout(patronSearchDebounceRef.current);
    };
  }, [filters.patronSearch, filters.patronMemberId, filters.patronMasterProfileId]);

  const clearPatronSearch = () => {
    setFilters((f) => ({
      ...f,
      patronSearch: '',
      patronMemberId: '',
      patronMasterProfileId: '',
    }));
    setPatronTypeahead({ results: [], loading: false, open: false });
  };

  const selectPatronMatch = (m) => {
    const mid =
      (m.MembershipNumber != null && String(m.MembershipNumber).trim()) ||
      (m.membership_number != null && String(m.membership_number).trim()) ||
      (m.member_id != null ? String(m.member_id) : '');
    const mpid = m.master_profile_id != null ? String(m.master_profile_id) : '';
    const label = [mid, m.first_name, m.last_name].filter(Boolean).join(' — ');
    setFilters((f) => ({
      ...f,
      patronSearch: label,
      patronMemberId: mid,
      patronMasterProfileId: mid ? '' : mpid,
    }));
    setPatronTypeahead({ results: [], loading: false, open: false });
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadPayoutData({ refreshRiskConfig: true });
    setRefreshing(false);
  };

  const closePayoutModal = () => {
    setSelectedPayout(null);
    setRevealBankAccount(false);
    setPayoutPatronCompliance(null);
    setPayoutComplianceLoading(false);
    setReviewCommentDraft('');
    setPayoutModalTab('details');
    setCaptureAttachments(null);
    setAttachmentsError(null);
    setAttachmentsLoading(false);
  };

  const loadPayoutPatronCompliance = async (memberId) => {
    const mid = String(memberId || '').trim();
    if (!mid) {
      setPayoutPatronCompliance(null);
      return;
    }
    setPayoutComplianceLoading(true);
    try {
      const r = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(mid)}/overview`));
      const j = await r.json();
      if (r.ok && j?.success && j.data) {
        setPayoutPatronCompliance({
          compliance_stage: j.data.compliance_stage,
          risk_score: j.data.risk_score,
          risk_level: j.data.risk_level,
          cdd_tier: j.data.cdd_tier,
          phone: j.data.profile?.phone || null,
        });
      } else {
        setPayoutPatronCompliance(null);
      }
    } catch (_) {
      setPayoutPatronCompliance(null);
    } finally {
      setPayoutComplianceLoading(false);
    }
  };

  const openPayoutDetail = async (payout) => {
    setSelectedPayout(withPayoutRiskFields(payout, txnRiskConfig));
    setRevealBankAccount(false);
    setPayoutPatronCompliance(null);
    setReviewCommentDraft(payout.commentReview || '');
    setPayoutModalTab('details');
    setCaptureAttachments(null);
    setAttachmentsError(null);
    setAttachmentsLoading(false);
    if (payout?.memberId || payout?.membershipNumber) {
      loadPayoutPatronCompliance(patronComplianceLookupKey(payout));
    }
    if (!payout?.id) return;
    try {
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts/${encodeURIComponent(payout.id)}`));
      const j = await r.json();
      if (r.ok && j.success && j.data) {
        setSelectedPayout(withPayoutRiskFields({
          ...payout,
          ...j.data,
          linkedCase: j.data.linkedCase ?? payout.linkedCase,
          hasCases: j.data.hasCases ?? payout.hasCases,
          hasSmrCase: j.data.hasSmrCase ?? payout.hasSmrCase,
          hasOpenEcdd: j.data.hasOpenEcdd ?? payout.hasOpenEcdd,
          workflowLabel: j.data.workflowLabel ?? payout.workflowLabel,
          cases: j.data.cases?.length ? j.data.cases : payout.cases,
          onWatchlist: j.data.onWatchlist ?? payout.onWatchlist,
          watchlistData: j.data.watchlistData ?? payout.watchlistData,
        }, txnRiskConfig));
        setReviewCommentDraft(j.data.commentReview || '');
        if (j.data.memberId || j.data.membershipNumber) {
          loadPayoutPatronCompliance(patronComplianceLookupKey(j.data));
        }
      }
    } catch (_) {
      /* keep list row data */
    }
  };

  const loadPayoutAttachments = useCallback(async (payoutId) => {
    if (!payoutId) return;
    setAttachmentsLoading(true);
    setAttachmentsError(null);
    try {
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts/${payoutId}/attachments`));
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to load attachments');
      setCaptureAttachments(j.data);
    } catch (e) {
      setCaptureAttachments(null);
      setAttachmentsError(e.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  }, []);

  const openAttachmentsTab = () => {
    setPayoutModalTab('attachments');
    if (selectedPayout?.id && !captureAttachments && !attachmentsLoading) {
      loadPayoutAttachments(selectedPayout.id);
    }
  };

  const getComplianceStatusBadge = (status) => {
    const s = String(status || '').toUpperCase();
    const badges = {
      COMPLIANT: 'bg-green-100 text-green-800',
      NON_COMPLIANT: 'bg-red-100 text-red-800',
      PENDING_REVIEW: 'bg-yellow-100 text-yellow-800',
      INVESTIGATING: 'bg-blue-100 text-blue-800',
      SMR_DRAFT: 'bg-purple-100 text-purple-800',
      SMR_FILED: 'bg-indigo-100 text-indigo-900',
      FLAGGED: 'bg-orange-100 text-orange-900',
    };
    return badges[s] || 'bg-gray-100 text-gray-800';
  };

  const openInvestigation = (payout) => {
    const cn = payout?.linkedCase?.caseNumber || payout?.cases?.[0]?.case_number;
    if (cn) navigate(`/compliance-safe/investigations/${encodeURIComponent(cn)}`);
    else if (payout?.linkedCase?.id) navigate(`/compliance-safe/investigations/${encodeURIComponent(payout.linkedCase.id)}`);
  };

  const getViolationTypeBadge = (type) => {
    const badges = {
      INSUFFICIENT_AUTHORIZATION: "bg-orange-100 text-orange-800",
      AUTHORIZATION_BYPASS: "bg-red-100 text-red-800",
      VENUE_TIER_VIOLATION: "bg-purple-100 text-purple-800",
      AMOUNT_THRESHOLD_EXCEEDED: "bg-pink-100 text-pink-800"
    };
    return badges[type] || "bg-gray-100 text-gray-800";
  };

  const handleCreateCase = async () => {
    if (!selectedPayout?.id) return;
    setCreatingCase(true);
    try {
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts/${selectedPayout.id}/create-case`), { method: 'POST' });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to create case');
      closePayoutModal();
      loadPayoutData();
      alert(`Case ${j.data?.case_number} created`);
    } catch (e) {
      alert(e.message || 'Failed to create case');
    } finally {
      setCreatingCase(false);
    }
  };

  const canStartEcddFromPayout =
    selectedPayout?.memberId &&
    selectedPayout.playerType !== 'visitor';

  const handleStartEcdd = async () => {
    if (!selectedPayout?.id) return;
    if (!canStartEcddFromPayout) {
      alert('ECDD requires a member ID. Visitor payouts cannot open ECDD from here.');
      return;
    }
    setCreatingEcdd(true);
    try {
      const tr = selectedPayout.transactionRisk;
      const r = await authFetch(
        getLocalApiUrl(`/api/monitoring/payouts/${selectedPayout.id}/create-ecdd`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_risk: tr
              ? {
                  score: tr.score,
                  level: tr.level,
                  factors: tr.factors,
                }
              : undefined,
          }),
        }
      );
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to create ECDD');
      const ecddId = j.data?.ecdd_id;
      if (!ecddId) throw new Error('Server did not return an ECDD id');
      closePayoutModal();
      loadPayoutData();
      if (j.data?.open_ecdd_warning) {
        alert(j.data.open_ecdd_warning);
      }
      const q = new URLSearchParams();
      if (selectedPayout.masterProfileId) q.set('patronId', selectedPayout.masterProfileId);
      if (selectedPayout.memberId) q.set('memberId', selectedPayout.memberId);
      if (selectedPayout.memberName) q.set('memberName', selectedPayout.memberName);
      const qs = q.toString();
      navigate(`/compliance-safe/ecdd/${encodeURIComponent(ecddId)}${qs ? `?${qs}` : ''}`);
    } catch (e) {
      alert(e.message || 'Failed to create ECDD');
    } finally {
      setCreatingEcdd(false);
    }
  };

  const handleDismiss = async () => {
    if (!selectedPayout?.id) return;
    if (!confirm('Mark this payout as reviewed and dismiss? It will be marked as COMPLIANT.')) return;
    setDismissing(true);
    try {
      const reviewText = reviewCommentDraft.trim();
      const comment_review = reviewText || `Dismissed by ${user?.name || user?.username || user?.sub || 'staff'} on ${new Date().toLocaleString()}`;
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts/${selectedPayout.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          compliance_status: 'COMPLIANT', 
          comment_review,
        })
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to dismiss');
      closePayoutModal();
      loadPayoutData();
      alert('Payout dismissed and marked as compliant');
    } catch (e) {
      alert(e.message || 'Failed to dismiss');
    } finally {
      setDismissing(false);
    }
  };

  const handleMiniInvestigation = async (payout, e) => {
    e.stopPropagation(); // Prevent row click
    if (payout.playerType !== 'member' || !payout.memberId) {
      alert('Member history is only available when a club member number was recorded on this payout.');
      return;
    }
    
    try {
      // Fetch recent payout history and gaming data
      const [payoutRes, sessionRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/monitoring/payouts?member_id=${payout.memberId}&limit=10`)),
        authFetch(getLocalApiUrl(`/api/watchlist/member/${payout.memberId}/overview`))
      ]);
      
      const payoutJson = await payoutRes.json();
      const sessionJson = await sessionRes.json();
      
      setMiniInvestigationData({
        payout,
        recentPayouts: payoutJson.success ? payoutJson.data : [],
        gamingData: sessionJson.success ? sessionJson.data : null
      });
      setShowMiniInvestigation(true);
    } catch (e) {
      alert('Failed to load investigation data: ' + e.message);
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 90) return "text-red-600";
    if (score >= 70) return "text-orange-600";
    if (score >= 40) return "text-yellow-600";
    return "text-green-600";
  };

  const kycBadge = (payoutOrStatus) => {
    const s = typeof payoutOrStatus === 'object' && payoutOrStatus != null
      ? payoutOrStatus.kycStatus
      : payoutOrStatus;
    const reduced =
      typeof payoutOrStatus === 'object' &&
      payoutOrStatus != null &&
      (payoutOrStatus.reducedKyc || payoutOrStatus.kycStatus === 'REDUCED');
    const idOnlyPending =
      typeof payoutOrStatus === 'object' &&
      payoutOrStatus?.faceEnrollmentStatus === 'IDOnlyPendingLiveCapture';
    if (!s && !reduced) return <span className="text-gray-400">—</span>;
    if (reduced || s === 'REDUCED') {
      return (
        <span className="inline-flex flex-col gap-0.5">
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-sky-100 text-sky-900">
            Reduced KYC
          </span>
          {idOnlyPending ? (
            <span className="text-[10px] leading-tight text-sky-800">ID only — live face pending</span>
          ) : null}
        </span>
      );
    }
    const cls = s === 'DONE' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800';
    return (
      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${cls}`}>
        {s === 'DONE' ? 'Done' : 'Not done'}
      </span>
    );
  };

  const addrBadge = (s) => {
    if (!s) return <span className="text-gray-400">—</span>;
    const map = { VERIFIED: "bg-emerald-100 text-emerald-800", NOT_VERIFIED: "bg-red-100 text-red-800", NA: "bg-gray-100 text-gray-700" };
    const cls = map[s] || "bg-gray-100 text-gray-700";
    const label = s === "VERIFIED" ? "Verified" : s === "NOT_VERIFIED" ? "Not verified" : "N/A";
    return <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${cls}`}>{label}</span>;
  };

  const trunc = (t, n = 48) => {
    if (t == null || t === "") return <span className="text-gray-400">—</span>;
    const s = String(t);
    if (s.length <= n) return <span className="text-gray-800">{s}</span>;
    return <span className="text-gray-800" title={s}>{s.slice(0, n)}…</span>;
  };

  const getAgingDisplay = (timestamp) => {
    if (!timestamp) return { text: '—', color: 'text-gray-400', urgent: false };
    
    const now = new Date();
    const issued = new Date(timestamp);
    const diffMs = now - issued;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    let text, color, urgent;
    
    if (diffHours < 1) {
      text = '<1h';
      color = 'text-green-700';
      urgent = false;
    } else if (diffHours < 24) {
      text = `${diffHours}h`;
      color = diffHours >= 12 ? 'text-amber-700' : 'text-green-700';
      urgent = diffHours >= 12;
    } else if (diffDays < 7) {
      text = `${diffDays}d`;
      color = diffDays >= 3 ? 'text-orange-700' : 'text-amber-700';
      urgent = diffDays >= 3;
    } else {
      const weeks = Math.floor(diffDays / 7);
      text = `${weeks}w`;
      color = 'text-red-700';
      urgent = true;
    }
    
    return { text, color, urgent };
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Filters are applied server-side; payoutData is already filtered
  // Apply sorting to payoutData
  const filteredPayouts = [...payoutData].sort((a, b) => {
    const key = sortConfig.key;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    
    let aVal = a[key];
    let bVal = b[key];
    
    // Special handling for timestamp (aging)
    if (key === 'timestamp') {
      aVal = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      bVal = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    }
    
    // Special handling for player type
    if (key === 'playerType') {
      aVal = a.memberId ? 'member' : 'visitor';
      bVal = b.memberId ? 'member' : 'visitor';
    }
    
    // Null/undefined handling
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    
    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction * aVal.localeCompare(bVal);
    }
    
    // Numeric comparison
    return direction * (aVal - bVal);
  });

  const pageTotal = listMeta.total != null ? Number(listMeta.total) : null
  const pageReturned = listMeta.returned ?? filteredPayouts.length
  const pageNum = listMeta.page || page
  const pageCount =
    listMeta.totalPages ||
    (pageTotal != null ? Math.max(1, Math.ceil(pageTotal / PAGE_SIZE)) : 1)
  const rangeStart = pageReturned > 0 ? (pageNum - 1) * PAGE_SIZE + 1 : 0
  const rangeEnd = pageTotal != null
    ? Math.min(pageNum * PAGE_SIZE, pageTotal)
    : (pageNum - 1) * PAGE_SIZE + pageReturned
  const hasMultiplePages =
    pageTotal != null
      ? pageTotal > PAGE_SIZE
      : pageReturned >= PAGE_SIZE || pageNum > 1

  const payoutPaginationControls = !loading && hasMultiplePages ? (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={pageNum <= 1 || loading}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white disabled:opacity-40 hover:bg-gray-50"
      >
        Previous
      </button>
      <span className="text-sm text-gray-500 px-1 whitespace-nowrap">
        {pageNum} / {pageCount}
      </span>
      <button
        type="button"
        disabled={pageNum >= pageCount || loading}
        onClick={() => setPage((p) => p + 1)}
        className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white disabled:opacity-40 hover:bg-gray-50"
      >
        Next
      </button>
    </div>
  ) : null

  const splitForModal =
    selectedPayout != null ? effectivePayoutSplit(selectedPayout) : null
  const mcForModal =
    selectedPayout?.mobileCapture && typeof selectedPayout.mobileCapture === 'object'
      ? selectedPayout.mobileCapture
      : {}
  const eftFromMc = formatEftSummary(mcForModal.eft_details)
  const chequeFromMc = formatChequeSummary(mcForModal.cheque_details)
  const declarationAcceptances = Array.isArray(mcForModal.declaration_acceptances)
    ? mcForModal.declaration_acceptances
    : []
  const attachmentCountHint = Number(selectedPayout?.captureAttachmentCount) || 0
  const showPayoutWorkflowActions = payoutMonitoringActionsAllowed(selectedPayout)
  const licenceImagesRemoved =
    captureAttachments?.licenceImagesRemoved === true ||
    mcForModal.licence_images_retention?.removed === true
  const patronPhoneDisplay = selectedPayout
    ? resolvePatronPhoneDisplay(selectedPayout, mcForModal, payoutPatronCompliance?.phone)
    : null
  const patronAddressText = selectedPayout
    ? effectivePatronAddressText(selectedPayout, mcForModal)
    : null
  const showBankVerified =
    selectedPayout &&
    (String(selectedPayout.paymentMethod || '').toLowerCase() === 'eft' ||
      selectedPayout.bankAccountPhysicallyVerified !== null)

  return (
    <div className="space-y-6">
      {/* Top toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payout Monitoring</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Review floor payouts — dismiss, investigate, or track SMR workflow
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <HelpScreenButton
            screenKey={PAYOUT_HELP_SCREEN_KEY}
            onClick={() => setHelpPanelOpen(true)}
          />
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Workflow queue tabs — each tab is a separate server-side listing with pagination */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap border-b border-gray-200">
          {QUEUE_TABS.map((tab) => {
            const active = filters.queueTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                title={tab.description}
                onClick={() => {
                  setPage(1);
                  setFilters((f) => ({ ...f, queueTab: tab.id }));
                }}
                className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                {active && listMeta.total != null && !loading && (
                  <span className="ml-2 text-xs font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                    {listMeta.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
          {QUEUE_TABS.find((t) => t.id === filters.queueTab)?.description}
        </p>
      </div>

      {/* Filters — one flex-wrap row; dates follow dropdowns and wrap with them */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
            <FunnelIcon className="w-5 h-5 text-gray-400 shrink-0" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <div className="relative w-full sm:w-auto sm:min-w-[14rem] sm:max-w-[20rem]">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={filters.patronSearch}
              onChange={(e) => setFilters((f) => ({
                ...f,
                patronSearch: e.target.value,
                patronMemberId: '',
                patronMasterProfileId: '',
              }))}
              placeholder="Patron ID or name"
              className="text-sm border border-gray-300 rounded-md pl-8 pr-8 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {(filters.patronSearch || filters.patronMemberId || filters.patronMasterProfileId) && (
              <button
                type="button"
                onClick={clearPatronSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear patron search"
              >
                <XCircleIcon className="w-4 h-4" />
              </button>
            )}
            {patronTypeahead.open && patronTypeahead.results.length > 0 && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {patronTypeahead.results.map((m) => (
                  <button
                    key={`${m.member_id}-${m.master_profile_id}`}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0"
                    onClick={() => selectPatronMatch(m)}
                  >
                    <span className="font-medium text-gray-900">
                      {displayMembershipNumber(m) || m.MembershipNumber || m.member_id}
                    </span>
                    {(m.first_name || m.last_name) && (
                      <span className="text-gray-600"> — {[m.first_name, m.last_name].filter(Boolean).join(' ')}</span>
                    )}
                    {m.master_profile_id && (
                      <span className="block text-[11px] text-indigo-600 font-mono">Patron {m.master_profile_id}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {patronTypeahead.loading && (
              <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">…</span>
            )}
          </div>

          <select
            value={filters.patronContext}
            onChange={(e) => setFilters({ ...filters, patronContext: e.target.value })}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[13rem]"
            title="Filter by patron compliance context"
          >
            <option value="all">All patrons</option>
            <option value="watchlist">On watchlist</option>
            <option value="smr">With SMR</option>
            <option value="ecdd">With open ECDD</option>
            <option value="investigation">With investigation</option>
          </select>

          <select
            value={venueNames.includes(filters.venue) || filters.venue === 'all' ? filters.venue : 'all'}
            onChange={(e) => setFilters({ ...filters, venue: e.target.value })}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:min-w-[10rem] sm:max-w-[14rem]"
            title="Venues from master data (venues table)"
          >
            <option value="all">All Venues</option>
            {venueNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={filters.payoutTier}
            onChange={(e) => setFilters({...filters, payoutTier: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[11rem]"
          >
            <option value="all">All tiers (PV1–PV4)</option>
            <option value="PV1">PV1 ($0–$2k)</option>
            <option value="PV2">PV2 ($2k–$5k)</option>
            <option value="PV3">PV3 ($5k–$10k)</option>
            <option value="PV4">PV4 ($10k+)</option>
          </select>

          <select
            value={filters.kycStatus}
            onChange={(e) => setFilters({...filters, kycStatus: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[9rem]"
          >
            <option value="all">All KYC</option>
            <option value="DONE">KYC Done</option>
            <option value="NOT_DONE">KYC Not Done</option>
            <option value="REDUCED">Reduced KYC</option>
          </select>

          <select
            value={filters.paymentMode}
            onChange={(e) => setFilters({ ...filters, paymentMode: e.target.value })}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[11rem]"
            aria-label="Payment mode"
          >
            <option value="all">All payment modes</option>
            <option value="cash">Cash only</option>
            <option value="eft">EFT only</option>
            <option value="cheque">Cheque only</option>
            <option value="split">Split (combination)</option>
            <option value="has_cash">Has cash</option>
            <option value="non_cash">Non-cash only</option>
          </select>

          <select
            value={filters.riskLevel}
            onChange={(e) => setFilters({...filters, riskLevel: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[11rem]"
          >
            <option value="all">All Risk Levels</option>
            <option value="high">High Risk (80+)</option>
            <option value="medium">Medium Risk (40-79)</option>
            <option value="low">Low Risk (&lt;40)</option>
          </select>

          <select
            value={filters.playerType}
            onChange={(e) => setFilters({...filters, playerType: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[10rem]"
          >
            <option value="all">All Players</option>
            <option value="member">Members Only</option>
            <option value="visitor">Visitors Only</option>
            <option value="unknown">Uncarded Only</option>
          </select>

          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
            <CalendarDaysIcon className="w-5 h-5 text-gray-400 shrink-0" />
            <input
              type="date"
              value={filters.startDate}
              onChange={(e)=>setFilters({...filters, startDate: e.target.value})}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 w-full sm:w-[9.5rem]"
            />
            <span className="text-xs text-gray-500 shrink-0">to</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e)=>setFilters({...filters, endDate: e.target.value})}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 w-full sm:w-[9.5rem]"
            />
          </div>
        </div>
      </div>

      {/* Payout Transactions — regulatory-style single view */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">Payout transactions</h3>
            <span className="inline-flex items-center gap-2 text-sm flex-wrap">
              <span className="font-semibold text-gray-800 bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded-md">
                {loading
                  ? 'Loading…'
                  : pageTotal != null && pageTotal > 0
                    ? `${rangeStart}–${rangeEnd} of ${pageTotal}`
                    : pageReturned >= PAGE_SIZE
                      ? `${rangeStart}–${rangeEnd} (page ${pageNum})`
                      : pageReturned > 0
                        ? `${pageReturned} payout${pageReturned === 1 ? '' : 's'}`
                        : '0 payouts'}
              </span>
              {!loading && pageTotal != null && hasMultiplePages && (
                <span className="text-gray-500">
                  page {pageNum} of {pageCount}
                </span>
              )}
            </span>
          </div>
          {payoutPaginationControls}
        </div>
        <div className="overflow-x-auto relative">
          {loading && (
            <div className="absolute inset-0 bg-white/60 z-30 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
          )}
          <table className="min-w-[1400px] w-full border-collapse divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => handleSort('venue')} className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[120px] min-w-[120px]">
                  Venue {getSortIcon('venue')}
                </th>
                <th
                  title="Patron profile risk from screening"
                  onClick={() => handleSort('riskScore')}
                  className="sticky left-[120px] z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[56px] min-w-[56px]"
                >
                  Profile {getSortIcon('riskScore')}
                </th>
                <th
                  title="Transaction risk: amount, cash mix, tier, win type"
                  onClick={() => handleSort('transactionRiskScore')}
                  className="sticky left-[176px] z-20 bg-gray-50 px-2 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[48px] min-w-[48px]"
                >
                  Txn {getSortIcon('transactionRiskScore')}
                </th>
                <th onClick={() => handleSort('timestamp')} className="sticky left-[224px] z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[80px] min-w-[80px]">
                  Aging {getSortIcon('timestamp')}
                </th>
                <th onClick={() => handleSort('playerType')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Player {getSortIcon('playerType')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Receipt</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Slip #</th>
                <th onClick={() => handleSort('timestamp')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Issued {getSortIcon('timestamp')}
                </th>
                <th onClick={() => handleSort('memberName')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Patron {getSortIcon('memberName')}
                </th>
                <th onClick={() => handleSort('amount')} className="px-3 py-2 text-right font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Amount {getSortIcon('amount')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Type</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">EGM Machine</th>
                <th onClick={() => handleSort('kycStatus')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  KYC {getSortIcon('kycStatus')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Address</th>
                <th onClick={() => handleSort('cashRatioPct')} className="px-3 py-2 text-right font-medium text-amber-900 bg-amber-50/80 border-r border-amber-200/80 cursor-pointer hover:bg-amber-100">
                  Cash ratio {getSortIcon('cashRatioPct')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-emerald-900 bg-emerald-50/80 border-r border-emerald-200/80">AML check</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 max-w-[140px]">Capture comment</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 max-w-[140px]">Review comment</th>
                <th onClick={() => handleSort('payoutTier')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Tier {getSortIcon('payoutTier')}
                </th>
                <th onClick={() => handleSort('complianceStatus')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100 min-w-[140px]">
                  Workflow {getSortIcon('complianceStatus')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayouts.map((payout) => {
                const atThreshold = payout.amount != null && Math.abs(Number(payout.amount) - 5000) < 0.01;
                const aging = getAgingDisplay(payout.timestamp);
                return (
                  <tr
                    key={payout.id}
                    onClick={() => openPayoutDetail(payout)}
                    className={`cursor-pointer ${atThreshold ? 'bg-orange-50/90' : ''}`}
                  >
                    <td className={`sticky left-0 z-20 px-3 py-2 border-r border-gray-300 whitespace-nowrap w-[120px] min-w-[120px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}>
                      <span className="text-gray-900 font-medium">{payout.venue}</span>
                    </td>
                    <td className={`sticky left-[120px] z-20 px-3 py-2 border-r border-gray-300 whitespace-nowrap w-[56px] min-w-[56px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}>
                      <span className={`font-bold ${getRiskScoreColor(effectivePatronRiskScore(payout))}`} title="Patron / profile risk">
                        {effectivePatronRiskScore(payout) ?? '—'}
                      </span>
                    </td>
                    <td
                      className={`sticky left-[176px] z-20 px-2 py-2 border-r border-gray-300 whitespace-nowrap w-[48px] min-w-[48px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}
                      title={payout.transactionRisk?.factors?.length ? payout.transactionRisk.factors.map((f) => f.detail).join(' · ') : 'Transaction risk'}
                    >
                      <span className={`font-bold ${payout.transactionRisk ? transactionRiskLevelColor(payout.transactionRisk.level) : 'text-gray-400'}`}>
                        {payout.transactionRisk?.score ?? '—'}
                      </span>
                    </td>
                    <td className={`sticky left-[224px] z-20 px-3 py-2 border-r border-gray-300 whitespace-nowrap w-[80px] min-w-[80px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}>
                      <span className={`font-bold ${aging.color} ${aging.urgent ? 'animate-pulse' : ''}`}>
                        {aging.text}
                      </span>
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${playerTypeBadgeClass(payout.playerType)}`}>
                        {playerTypeLabel(payout.playerType)}
                      </span>
                      {(payout.enrichmentFlags || []).some((f) => f.code === 'member_posed_as_visitor') && (
                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-900 ml-1" title="DimPatron name match — member card not used">
                          No card
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.receiptRef || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 font-mono whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.slipNumber || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-700 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      {payout.timestamp ? new Date(payout.timestamp).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 min-w-[140px] ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      <div className="text-gray-900 font-medium flex items-center gap-2 flex-wrap">
                        {payout.memberName}
                        {payout.patronNameMismatch && (
                          <span
                            className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-900"
                            title={payout.patronNameMismatchNote || 'Payout name differs from patron profile'}
                          >
                            Name mismatch
                          </span>
                        )}
                        {payout.onWatchlist && (
                          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" title="On Watchlist" />
                        )}
                        {payout.hasSmrCase && (
                          <ShieldExclamationIcon className="w-4 h-4 text-red-600" title="SMR / suspicious activity case" />
                        )}
                        {payout.hasOpenEcdd && (
                          <DocumentMagnifyingGlassIcon className="w-4 h-4 text-purple-600" title="Open ECDD" />
                        )}
                        {payout.hasCases && !payout.hasSmrCase && (
                          <FolderOpenIcon className="w-4 h-4 text-blue-600" title={`${payout.caseCount} case(s)`} />
                        )}
                      </div>
                      {effectiveMasterProfileId(payout) ? (
                        <div className="text-[11px] font-mono font-semibold text-indigo-700 mt-0.5">
                          {effectiveMasterProfileId(payout)}
                        </div>
                      ) : (
                        <div className="text-[11px] text-amber-700 mt-0.5">No patron ID</div>
                      )}
                      <div className="text-gray-500 text-[10px] mt-0.5">{clubIdSubtext(payout)}</div>
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-right font-semibold text-gray-900 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(payout.amount)}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.gameType || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 font-mono whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.egmId || payout.gameId || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{kycBadge(payout)}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{addrBadge(payout.addressVerification)}</td>
                    <td className={`px-3 py-2 border-r border-amber-200/80 text-right bg-amber-50/50 text-amber-950 whitespace-nowrap ${atThreshold ? '!bg-orange-50/90' : ''}`}>
                      {payout.cashRatioPct != null ? `${Number(payout.cashRatioPct).toFixed(2)}%` : '—'}
                    </td>
                    <td className={`px-3 py-2 border-r border-emerald-200/80 bg-emerald-50/50 text-emerald-950 max-w-[200px] ${atThreshold ? '!bg-orange-50/90' : ''}`}>
                      {payout.amlRisk ? <span title={payout.amlRisk}>{trunc(payout.amlRisk, 64)}</span> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 max-w-[160px] ${atThreshold ? 'bg-orange-50/90' : ''}`}>{trunc(payout.commentCapture, 56)}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 max-w-[160px] ${atThreshold ? 'bg-orange-50/90' : ''}`}>{trunc(payout.commentReview, 56)}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-700 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.payoutTier}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getComplianceStatusBadge(payout.complianceStatus)}`}>
                        {payout.workflowLabel || payout.complianceStatus?.replace(/_/g, ' ') || '—'}
                      </span>
                      {(payout.linkedCase?.caseNumber || payout.cases?.[0]?.case_number) && (
                        <button
                          type="button"
                          onClick={() => openInvestigation(payout)}
                          className="block text-[10px] text-blue-700 hover:underline mt-0.5 font-mono"
                        >
                          {payout.linkedCase?.caseNumber || payout.cases?.[0]?.case_number}
                        </button>
                      )}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      {payout.complianceStatus === 'NON_COMPLIANT' && (
                        <ShieldExclamationIcon className="w-4 h-4 inline text-red-500" title="Non-compliant" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && hasMultiplePages && (
          <div className="px-6 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-gray-50 sticky bottom-0">
            <span className="text-sm text-gray-600">
              {pageTotal != null
                ? `Showing ${rangeStart}–${rangeEnd} of ${pageTotal} payouts`
                : `Page ${pageNum} of ${pageCount}`}
            </span>
            {payoutPaginationControls}
          </div>
        )}
      </div>

      {/* Payout Detail Modal - Redesigned */}
      {selectedPayout && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 px-4 sm:px-6">
          <div className="relative top-8 mx-auto p-5 border w-full max-w-[75rem] shadow-2xl rounded-lg bg-white mb-8">
            {/* Header with badges */}
            <div className="flex justify-between items-start mb-4 pb-3 border-b border-gray-300">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Payout Details - {selectedPayout.id}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-semibold text-gray-700">{selectedPayout.memberName}</span>
                  {selectedPayout.patronNameMismatch && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-900 rounded-full border border-amber-300">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      NAME MISMATCH
                    </span>
                  )}
                  {selectedPayout.onWatchlist && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-800 rounded-full border border-amber-300">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      WATCHLIST
                    </span>
                  )}
                  {selectedPayout.hasSmrCase && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-red-100 text-red-800 rounded-full border border-red-300">
                      <ShieldExclamationIcon className="w-3 h-3 mr-1" />
                      SMR
                    </span>
                  )}
                  {selectedPayout.hasOpenEcdd && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-purple-100 text-purple-800 rounded-full border border-purple-300">
                      <DocumentMagnifyingGlassIcon className="w-3 h-3 mr-1" />
                      ECDD
                    </span>
                  )}
                  {selectedPayout.hasCases && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-800 rounded-full border border-blue-300">
                      <FolderOpenIcon className="w-3 h-3 mr-1" />
                      {selectedPayout.caseCount} CASE{selectedPayout.caseCount > 1 ? 'S' : ''}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${playerTypeBadgeClass(selectedPayout.playerType)}`}>
                    {playerTypeLabel(selectedPayout.playerType)}
                  </span>
                  <span>{patronIdSummary(selectedPayout)}</span>
                </div>
                {selectedPayout.patronNameMismatch && (
                  <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1 leading-snug">
                    {selectedPayout.patronNameMismatchNote ||
                      `Payout name "${selectedPayout.payoutCaptureName || selectedPayout.memberName}" differs from patron profile "${selectedPayout.patronProfileName}".`}
                    {selectedPayout.patronProfileName && (
                      <span className="block mt-1 text-amber-800">
                        Patron profile: <strong>{selectedPayout.patronProfileName}</strong>
                      </span>
                    )}
                  </div>
                )}
                {(selectedPayout.enrichmentFlags || [])
                  .filter((f) => f.code !== 'patron_profile_name_mismatch')
                  .map((f) => (
                  <div key={f.code} className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1 mt-1">
                    {f.message}
                  </div>
                ))}
              </div>
              <button
                onClick={closePayoutModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2 mb-4 border-b border-gray-200">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPayoutModalTab('details')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    payoutModalTab === 'details'
                      ? 'border-psg-teal text-psg-teal'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Details
                </button>
                <button
                  type="button"
                  onClick={openAttachmentsTab}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    payoutModalTab === 'attachments'
                      ? 'border-psg-teal text-psg-teal'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <PaperClipIcon className="w-4 h-4" />
                  Attachments
                  {attachmentCountHint > 0 && (
                    <span className="text-xs bg-gray-100 text-gray-700 rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                      {attachmentCountHint}
                    </span>
                  )}
                </button>
              </div>
              {showPayoutWorkflowActions ? (
                <div className="flex flex-wrap justify-end gap-2 pb-2 ml-auto">
                  <button
                    onClick={handleDismiss}
                    disabled={dismissing}
                    className="px-3 py-1.5 bg-white border border-green-600 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50 font-semibold text-xs sm:text-sm whitespace-nowrap"
                  >
                    {dismissing ? 'Dismissing…' : 'Dismiss'}
                  </button>
                  <button
                    onClick={handleCreateCase}
                    disabled={creatingCase || creatingEcdd}
                    className="px-3 py-1.5 bg-white border border-psg-teal text-psg-teal rounded-lg hover:bg-teal-50 disabled:opacity-50 font-semibold text-xs sm:text-sm whitespace-nowrap"
                  >
                    {creatingCase ? 'Starting…' : 'Start Investigation'}
                  </button>
                  <button
                    type="button"
                    onClick={handleStartEcdd}
                    disabled={creatingEcdd || creatingCase || !canStartEcddFromPayout}
                    title={
                      canStartEcddFromPayout
                        ? 'Open a standalone ECDD pre-filled from this payout'
                        : 'ECDD requires a member payout (not visitor-only)'
                    }
                    className="px-3 py-1.5 bg-white border border-violet-600 text-violet-700 rounded-lg hover:bg-violet-50 disabled:opacity-50 font-semibold text-xs sm:text-sm whitespace-nowrap"
                  >
                    {creatingEcdd ? 'Starting…' : 'Start ECDD'}
                  </button>
                  <button
                    onClick={closePayoutModal}
                    className="px-3 py-1.5 bg-white border border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-xs sm:text-sm whitespace-nowrap"
                  >
                    Close
                  </button>
                </div>
              ) : selectedPayout?.workflowLabel ? (
                <p className="text-xs text-gray-500 pb-2 ml-auto max-w-md text-right">
                  {selectedPayout.workflowLabel} — actions are managed in the linked case or compliance workflow.
                </p>
              ) : null}
            </div>

            {payoutModalTab === 'attachments' ? (
              <div className="mb-4 min-h-[200px]">
                {attachmentsLoading && (
                  <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                    <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                    Loading images…
                  </div>
                )}
                {!attachmentsLoading && attachmentsError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {attachmentsError}
                    <button
                      type="button"
                      onClick={() => loadPayoutAttachments(selectedPayout.id)}
                      className="block mt-2 text-psg-teal underline"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {!attachmentsLoading && !attachmentsError && captureAttachments?.licenceImagesRemoved && (
                  <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
                    Licence images were removed after DVS per venue retention policy
                    {captureAttachments.retentionPolicy
                      ? ` (${String(captureAttachments.retentionPolicy).replace(/_/g, ' ')})`
                      : ''}
                    .
                  </p>
                )}
                {!attachmentsLoading && !attachmentsError && (
                  <>
                    {Array.isArray(captureAttachments?.attachments) &&
                    captureAttachments.attachments.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {captureAttachments.attachments.map((tile) => (
                          <div
                            key={tile.key}
                            className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm"
                          >
                            <div
                              className="text-xs text-slate-600 px-2 py-1.5 border-b border-slate-100 truncate font-medium"
                              title={tile.label}
                            >
                              {tile.label}
                            </div>
                            <a
                              href={tile.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block bg-slate-100"
                            >
                              <img
                                src={tile.href}
                                alt={tile.label}
                                className="w-full h-36 object-contain"
                              />
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-8 text-center">
                        No capture images on file for this payout.
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
            <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {/* LEFT: Payment receipt */}
              <div className="col-span-1 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-300 shadow-sm">
                <div className="text-center mb-3 pb-2 border-b border-dashed border-gray-400">
                  <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Payment Receipt</h4>
                  <div className="text-2xl font-bold text-green-700">
                    {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(selectedPayout.amount)}
                  </div>
                  <div className="text-xs text-gray-500">{selectedPayout.payoutTier}</div>
                </div>
                
                {/* Payment method + split (DB columns or derived from mobile_capture_json) */}
                <div className="mb-3 pb-3 border-b border-gray-300">
                  <h5 className="text-xs font-bold text-gray-600 uppercase mb-1.5">Payment</h5>
                  {splitForModal || mcForModal.payment_method || selectedPayout.paymentMethod ? (
                    <div className="divide-y divide-gray-300 text-xs">
                      {(mcForModal.payment_method || selectedPayout.paymentMethod) && (
                        <PayoutDetailRow label="Method" valueClassName="font-semibold">
                          {String(mcForModal.payment_method || selectedPayout.paymentMethod || '—')}
                        </PayoutDetailRow>
                      )}
                      {splitForModal?.cash > 0 && (
                        <PayoutDetailRow
                          label="Cash"
                          valueClassName={`font-semibold ${splitForModal.cash > 5000 ? 'text-red-600' : ''}`}
                        >
                          {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(splitForModal.cash)}
                          {splitForModal.cash > 5000 && (
                            <span className="ml-1 text-red-600 font-bold">⚠ {'>$5k'}</span>
                          )}
                        </PayoutDetailRow>
                      )}
                      {splitForModal?.eft > 0 && (
                        <PayoutDetailRow label="EFT" valueClassName="font-semibold">
                          {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(splitForModal.eft)}
                        </PayoutDetailRow>
                      )}
                      {splitForModal?.cheque > 0 && (
                        <PayoutDetailRow label="Cheque" valueClassName="font-semibold">
                          {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(splitForModal.cheque)}
                        </PayoutDetailRow>
                      )}
                      {splitForModal &&
                        splitForModal.cash === 0 &&
                        splitForModal.eft === 0 &&
                        splitForModal.cheque === 0 && (
                          <p className="py-1.5 text-gray-500">No cash / EFT / cheque amounts recorded.</p>
                        )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No payment split on file for this payout.</p>
                  )}
                  {(eftFromMc || chequeFromMc) && (
                    <div className="mt-2 pt-2 border-t border-gray-200 divide-y divide-gray-200 text-[11px] text-gray-700">
                      {eftFromMc && (
                        <PayoutDetailRow label="EFT details" labelClassName="font-semibold">
                          <span className="inline-flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                            {eftFromMc.prefix ? (
                              <span>
                                {eftFromMc.prefix}
                                {(eftFromMc.accountNumber || eftFromMc.accountLast4) ? ' · ' : ''}
                              </span>
                            ) : null}
                            {(eftFromMc.accountNumber || eftFromMc.accountLast4) && (
                              <RevealableBankAccount
                                prefix="Acct "
                                fullAccount={eftFromMc.accountNumber}
                                last4={eftFromMc.accountLast4 || selectedPayout.bankAccountLast4}
                                revealed={revealBankAccount}
                                onToggle={() => setRevealBankAccount((v) => !v)}
                              />
                            )}
                          </span>
                        </PayoutDetailRow>
                      )}
                      {chequeFromMc && (
                        <PayoutDetailRow label="Cheque" labelClassName="font-semibold">
                          {chequeFromMc}
                        </PayoutDetailRow>
                      )}
                    </div>
                  )}
                  {(selectedPayout.bankName || selectedPayout.bankAccountLast4 || eftFromMc?.accountNumber) && (
                    <div className="mt-2 pt-2 border-t border-gray-200 divide-y divide-gray-200 text-xs text-gray-600">
                      {selectedPayout.bankName && (
                        <PayoutDetailRow label="Bank" valueClassName="font-medium">
                          {selectedPayout.bankName}
                        </PayoutDetailRow>
                      )}
                      {(selectedPayout.bankAccountLast4 || eftFromMc?.accountNumber) && (
                        <PayoutDetailRow label="Account" valueClassName="font-mono">
                          <RevealableBankAccount
                            fullAccount={eftFromMc?.accountNumber}
                            last4={selectedPayout.bankAccountLast4 || eftFromMc?.accountLast4}
                            revealed={revealBankAccount}
                            onToggle={() => setRevealBankAccount((v) => !v)}
                          />
                        </PayoutDetailRow>
                      )}
                    </div>
                  )}
                  {selectedPayout.paymentBehaviourChange?.hasChange && (
                    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2.5">
                      <h5 className="text-xs font-bold text-amber-900 uppercase mb-1.5">
                        Payout Behaviour Change
                      </h5>
                      {selectedPayout.paymentBehaviourChange.priorPayoutId && (
                        <p className="text-[11px] text-amber-800 mb-1.5">
                          Compared to prior payout{' '}
                          <span className="font-mono font-semibold">
                            {selectedPayout.paymentBehaviourChange.priorPayoutId}
                          </span>
                        </p>
                      )}
                      <ul className="space-y-1.5">
                        {(selectedPayout.paymentBehaviourChange.changes || []).map((ch) => (
                          <li key={ch.code} className="text-[11px] text-amber-950">
                            <span className="font-semibold">{ch.label}</span>
                            <span className="text-amber-800">
                              {' '}
                              — {ch.previous} → {ch.current}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="divide-y divide-gray-300 text-xs">
                  <PayoutDetailRow label="Receipt" valueClassName="font-mono">
                    {selectedPayout.receiptRef || '—'}
                  </PayoutDetailRow>
                  <PayoutDetailRow label="Slip" valueClassName="font-mono">
                    {selectedPayout.slipNumber || '—'}
                  </PayoutDetailRow>
                  <PayoutDetailRow label="Issued">
                    {selectedPayout.timestamp ? new Date(selectedPayout.timestamp).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </PayoutDetailRow>
                  <PayoutDetailRow label="Venue" valueClassName="font-semibold">
                    {selectedPayout.venue}
                  </PayoutDetailRow>
                  <PayoutDetailRow label="Game">
                    {selectedPayout.gameType || '—'}
                  </PayoutDetailRow>
                  <PayoutDetailRow label="EGM Machine" valueClassName="font-mono">
                    {selectedPayout.egmId || selectedPayout.gameId || '—'}
                  </PayoutDetailRow>
                  <PayoutDetailRow label="Duration" valueClassName="font-semibold">
                    {selectedPayout.sessionDuration}
                  </PayoutDetailRow>
                </div>
              </div>

              {/* MIDDLE: Risk & compliance */}
              <div className="col-span-1">
                <div className="bg-white rounded-lg p-2.5 border border-gray-200 shadow-sm space-y-2.5 h-full">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Risk &amp; compliance</h4>

                  <div className="rounded-md border border-gray-200 bg-slate-50/80 p-2.5 space-y-2">
                    <div className="text-xs font-semibold text-gray-700">Patron risk</div>
                    {selectedPayout.memberId && selectedPayout.playerType !== 'visitor' ? (
                      <>
                        {payoutComplianceLoading ? (
                          <span className="text-[10px] text-gray-500">Loading patron context…</span>
                        ) : (
                          (() => {
                            const patronRisk = patronRiskScoreForDisplay(
                              selectedPayout,
                              payoutPatronCompliance
                            )
                            return (
                              <>
                                <PatronComplianceStrip
                                  size="compact"
                                  complianceStage={payoutPatronCompliance?.compliance_stage}
                                  riskScore={patronRisk}
                                  riskLevel={patronRiskLevelForDisplay(
                                    selectedPayout,
                                    payoutPatronCompliance
                                  )}
                                  cddTier={payoutPatronCompliance?.cdd_tier}
                                />
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${(patronRisk ?? 0) >= 90 ? 'bg-red-600' : (patronRisk ?? 0) >= 70 ? 'bg-orange-500' : (patronRisk ?? 0) >= 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                    style={{ width: `${Math.min(100, Number(patronRisk) || 0)}%` }}
                                  />
                                </div>
                              </>
                            )
                          })()
                        )}
                      </>
                    ) : (
                      <p className="text-[10px] text-gray-500">Visitor or uncarded payout — no member patron risk profile.</p>
                    )}
                  </div>

                  {(() => {
                    const txnRisk =
                      selectedPayout.transactionRisk ||
                      computeTransactionRisk(selectedPayout, effectivePayoutSplit(selectedPayout), txnRiskConfig)
                    return (
                      <div className="rounded-md border border-gray-200 bg-slate-50/80 p-2.5 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-700 shrink-0">Transaction risk</span>
                        <span className={`text-xs font-bold ${transactionRiskLevelColor(txnRisk.level)}`}>
                          {txnRisk.score} {txnRisk.level}
                        </span>
                      </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${transactionRiskBarClass(txnRisk.score)}`}
                            style={{ width: `${txnRisk.score}%` }}
                          />
                        </div>
                        {txnRisk.suggestInvestigate && (
                          <span className="inline-flex text-[10px] font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                            Review suggested
                          </span>
                        )}
                        {txnRisk.factors?.length > 0 && (
                          <ul className="text-[10px] text-gray-700 space-y-0.5 pl-3 list-disc marker:text-gray-400">
                            {txnRisk.factors.map((f) => (
                              <li key={f.code}>
                                <span className="font-medium">{f.label}:</span> {f.detail}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )
                  })()}

                  <div className="pt-2 border-t border-gray-200 divide-y divide-gray-200 text-xs">
                    <PayoutDetailRow label="Compliance" labelClassName="font-semibold">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full ${getComplianceStatusBadge(selectedPayout.complianceStatus)}`}>
                        {(selectedPayout.complianceStatus || '—').replace(/_/g, ' ')}
                      </span>
                    </PayoutDetailRow>
                    {(selectedPayout.verificationQueueId || selectedPayout.queueDvsStatus) && (
                      <div className="py-1.5 text-[10px] text-teal-800 bg-teal-50 rounded px-2 border border-teal-100">
                        Queue #{selectedPayout.verificationQueueId}: DVS {selectedPayout.queueDvsStatus || '—'}, AML {selectedPayout.queueAmlStatus || '—'}
                        {selectedPayout.queueOnboardingStatus ? ` (${String(selectedPayout.queueOnboardingStatus).replace(/_/g, ' ')})` : ''}
                      </div>
                    )}
                    <PayoutDetailRow label="Cash ratio" valueClassName="font-bold text-amber-700">
                      {selectedPayout.cashRatioPct != null ? `${Number(selectedPayout.cashRatioPct).toFixed(2)}%` : '—'}
                    </PayoutDetailRow>
                    <PayoutDetailRow label="AML" valueClassName="text-emerald-800 font-medium">
                      {selectedPayout.amlRisk || 'No issues'}
                    </PayoutDetailRow>
                  </div>

                  {selectedPayout.onWatchlist && selectedPayout.watchlistData && (
                    <div className="pt-2 border-t border-amber-200 text-xs">
                      <div className="font-bold text-amber-800 flex items-center gap-1 mb-1">
                        <ExclamationTriangleIcon className="w-3 h-3" />
                        Watchlist
                      </div>
                      <div className="divide-y divide-amber-200/60">
                        <PayoutDetailRow label="Added" rowClassName="text-xs" labelClassName="text-amber-800" valueClassName="text-amber-800 font-semibold">
                          {selectedPayout.watchlistData.first_seen ? new Date(selectedPayout.watchlistData.first_seen).toLocaleDateString('en-AU') : '—'}
                        </PayoutDetailRow>
                      </div>
                      {selectedPayout.watchlistData.reason && (
                        <div className="text-amber-800 mt-0.5 italic">&ldquo;{selectedPayout.watchlistData.reason}&rdquo;</div>
                      )}
                    </div>
                  )}

                  {selectedPayout.hasCases && (
                    <div className="pt-2 border-t border-blue-200 max-h-32 overflow-y-auto">
                      <div className="text-xs font-bold text-blue-800 flex items-center gap-1 mb-1">
                        <FolderOpenIcon className="w-3 h-3" />
                        Cases ({selectedPayout.caseCount})
                      </div>
                      <div className="space-y-1">
                        {selectedPayout.cases.slice(0, 4).map((c, idx) => (
                          <div key={idx} className="text-[10px] bg-blue-50 rounded px-2 py-1 border border-blue-100">
                            <span className="font-bold text-blue-900">{c.case_number || c.id}</span>
                            <span className="text-gray-600"> · {c.case_type} · {c.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: Patron, KYC & capture */}
              <div className="col-span-1">
                <div className="bg-white rounded-lg p-2.5 border border-gray-200 shadow-sm space-y-2.5 h-full">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Patron &amp; capture</h4>

                  {(selectedPayout.visitId || selectedPayout.visitorId) && (
                    <div className="text-xs divide-y divide-gray-200">
                      {selectedPayout.visitId && (
                        <PayoutDetailRow label="Visit ID" valueClassName="font-mono">{selectedPayout.visitId}</PayoutDetailRow>
                      )}
                      {selectedPayout.visitorId && (
                        <PayoutDetailRow label="VMS ID" valueClassName="font-mono">{selectedPayout.visitorId}</PayoutDetailRow>
                      )}
                    </div>
                  )}

                  {selectedPayout.patronResolve && (() => {
                    const fmt = formatPatronResolvePatron(selectedPayout.patronResolve)
                    const pr = selectedPayout.patronResolve
                    return (
                      <div className="text-xs pt-2 border-t border-gray-100 divide-y divide-gray-200">
                        {fmt?.name && <PayoutDetailRow label="Name">{fmt.name}</PayoutDetailRow>}
                        {fmt?.dob && <PayoutDetailRow label="DOB">{fmt.dob}</PayoutDetailRow>}
                        {pr.lookup_type && (
                          <PayoutDetailRow label="Lookup">
                            {pr.lookup_type} {pr.lookup_value || ''}
                          </PayoutDetailRow>
                        )}
                      </div>
                    )
                  })()}

                  {selectedPayout.dimPatron && (
                    <div className="text-xs pt-2 border-t border-gray-100 divide-y divide-gray-200">
                      <PayoutDetailRow label="DimPatron">
                        {[selectedPayout.dimPatron.first_name, selectedPayout.dimPatron.last_name].filter(Boolean).join(' ')}
                      </PayoutDetailRow>
                      {selectedPayout.dimPatron.name_mismatch && (
                        <div className="py-1.5 text-amber-800 font-medium text-[10px]">Name mismatch vs directory</div>
                      )}
                    </div>
                  )}

                  <div className="text-xs pt-2 border-t border-gray-200 divide-y divide-gray-200">
                    <PayoutDetailRow label="Patron phone">
                      {patronPhoneDisplay?.status === 'declined' ? (
                        <span className="text-amber-800 font-medium">Declined</span>
                      ) : patronPhoneDisplay?.status === 'captured' ? (
                        <span className="text-emerald-800 font-medium">Captured</span>
                      ) : patronPhoneDisplay?.status === 'on_record' ? (
                        <span className="text-emerald-800 font-medium">On record</span>
                      ) : patronPhoneDisplay?.status === 'not_captured' ? (
                        <span className="text-gray-600">Not captured</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </PayoutDetailRow>
                    {patronPhoneDisplay?.phone ? (
                      <PayoutDetailRow label="Number" valueClassName="font-mono">
                        {patronPhoneDisplay.phone}
                        {patronPhoneDisplay.hint ? (
                          <span className="block text-[10px] text-gray-500 font-sans font-normal mt-0.5">
                            {patronPhoneDisplay.hint}
                          </span>
                        ) : null}
                      </PayoutDetailRow>
                    ) : null}
                    {showBankVerified && (
                      <PayoutDetailRow label="Bank verified">
                        {selectedPayout.bankAccountPhysicallyVerified === true ? (
                          <span className="text-emerald-800 font-medium">Yes</span>
                        ) : selectedPayout.bankAccountPhysicallyVerified === false ? (
                          <span className="text-amber-800 font-medium">No</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </PayoutDetailRow>
                    )}
                    {patronAddressText ? (
                      <PayoutDetailRow label="Address" valueClassName="whitespace-pre-wrap">
                        {patronAddressText}
                      </PayoutDetailRow>
                    ) : null}
                    {selectedPayout.occupation ? (
                      <PayoutDetailRow label="Occupation">
                        {selectedPayout.occupation}
                      </PayoutDetailRow>
                    ) : null}
                  </div>

                  <div className="pt-2 border-t border-gray-200 divide-y divide-gray-200">
                    <PayoutDetailRow label="KYC" rowClassName="text-xs" labelClassName="text-[10px] font-semibold">
                      <div>
                        {kycBadge(selectedPayout)}
                        {selectedPayout.reducedKycHydratedFromRegistryId ? (
                          <div className="text-[10px] text-sky-800 mt-0.5">
                            Prior KYC #{selectedPayout.reducedKycHydratedFromRegistryId}
                          </div>
                        ) : null}
                      </div>
                    </PayoutDetailRow>
                    <PayoutDetailRow label="Address verify" rowClassName="text-xs" labelClassName="text-[10px] font-semibold">
                      {addrBadge(selectedPayout.addressVerification)}
                    </PayoutDetailRow>
                    <div className="py-1.5">
                      <OcrExtractPanel
                        compact
                        ocrData={
                          selectedPayout.effectiveOcrData ||
                          selectedPayout.payoutOcrData ||
                          selectedPayout.queueOcrData
                        }
                        title={
                          selectedPayout.reducedKyc
                            ? 'Document (prior KYC)'
                            : 'Document'
                        }
                        subtitle={
                          selectedPayout.effectiveOcrSources?.length
                            ? selectedPayout.effectiveOcrSources.slice(0, 2).join(', ') +
                              (selectedPayout.effectiveOcrSources.length > 2 ? '…' : '')
                            : selectedPayout.reducedKycHydratedFromRegistryId
                              ? `KYC #${selectedPayout.reducedKycHydratedFromRegistryId}`
                              : undefined
                        }
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100 space-y-2">
                    <div>
                      <div className="text-[10px] font-semibold text-gray-600 mb-1">Floor comment</div>
                      <div className="text-xs text-gray-800 bg-gray-50 rounded border border-gray-200 px-2 py-1.5 min-h-[2.5rem] whitespace-pre-wrap break-words">
                        {selectedPayout.commentCapture || <span className="text-gray-400 italic">None</span>}
                      </div>
                      {selectedPayout.patronAddressFromCapture && (
                        <p className="text-[10px] text-gray-500 mt-1 whitespace-pre-wrap break-words">
                          Address (capture): {selectedPayout.patronAddressFromCapture}
                        </p>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold text-gray-600 mb-1">Compliance review</div>
                      <textarea
                        value={reviewCommentDraft}
                        onChange={(e) => setReviewCommentDraft(e.target.value)}
                        placeholder="Add compliance review notes…"
                        className="w-full bg-white rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-800 min-h-[3.5rem] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {declarationAcceptances.length > 0 && (
                    <div className="pt-2 border-t border-gray-200 divide-y divide-gray-200">
                      <div className="text-[10px] font-semibold text-gray-600 py-1.5">Declarations</div>
                      {declarationAcceptances.map((row, idx) => {
                        const label = row.label || row.role_id || `Role ${idx + 1}`
                        const accepted = row.accepted === true
                        return (
                          <PayoutDetailRow
                            key={String(row.slot_key ?? row.role_id ?? idx)}
                            label={label}
                            rowClassName="text-[10px]"
                            labelClassName="text-gray-800 font-normal"
                            valueClassName={accepted ? 'text-emerald-800 font-medium' : 'text-amber-800 font-medium'}
                          >
                            {accepted ? 'Accepted' : 'Pending'}
                            {row.accepted_at ? ` · ${formatDeclarationAcceptedAt(row.accepted_at)}` : ''}
                          </PayoutDetailRow>
                        )
                      })}
                    </div>
                  )}

                  {(attachmentCountHint > 0 || (selectedPayout.signatureCount != null && selectedPayout.signatureCount > 0)) && (
                    <button
                      type="button"
                      onClick={openAttachmentsTab}
                      className="text-[10px] text-psg-teal font-medium hover:underline inline-flex items-center gap-1"
                    >
                      <PaperClipIcon className="w-3.5 h-3.5" />
                      {attachmentCountHint > 0 ? `${attachmentCountHint} attachment${attachmentCountHint === 1 ? '' : 's'}` : 'Attachments'}
                    </button>
                  )}

                  {licenceImagesRemoved && (
                    <div className="text-[10px] text-slate-600">Licence images removed (retention policy)</div>
                  )}
                </div>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      <ScreenHelpPanel
        screenKey={PAYOUT_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        focusSectionId={primaryHelpSectionForPayoutMonitoring({
          selectedPayout,
          payoutModalTab,
          queueTab: filters.queueTab,
          workflowActionsVisible: showPayoutWorkflowActions,
        })}
        sectionIdPrefix="payout-monitoring-help"
      />
    </div>
  );
};

export default PayoutMonitoring;